import React, { useLayoutEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { FormikInvoice } from "utils/invoices/invoiceFormat";
import InvoicePreview from "./InvoicePreview";

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
/** A4's height over its width: the thumbnail frames the first page however long the invoice runs. */
const A4_RATIO = A4_HEIGHT_MM / A4_WIDTH_MM;

interface InvoicePreviewThumbnailProps {
  invoice: FormikInvoice;
}

/** The live invoice's first page, scaled to fit its container and centered in it. */
const InvoicePreviewThumbnail: React.FC<InvoicePreviewThumbnailProps> = ({
  invoice,
}) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState<{ scale: number; pageWidth: number } | null>(
    null,
  );

  useLayoutEffect(() => {
    const frame = frameRef.current;
    const page = pageRef.current;
    if (!frame || !page) return;
    // offsetWidth ignores the transform, so the page always reads as its full A4 width.
    const measure = () => {
      const pageWidth = page.offsetWidth;
      if (!pageWidth || !frame.clientWidth || !frame.clientHeight) return;
      setFit({
        pageWidth,
        scale: Math.min(
          frame.clientWidth / pageWidth,
          frame.clientHeight / (pageWidth * A4_RATIO),
        ),
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    return () => observer.disconnect();
  }, []);

  const sheetWidth = fit ? fit.pageWidth * fit.scale : 0;

  return (
    <Box
      ref={frameRef}
      aria-hidden
      data-testid="invoice-preview-thumbnail"
      sx={{
        position: "relative",
        width: "100%",
        // Alone on a phone the frame takes the page's own shape; beside the
        // notes it is a floor the row stretches, so it stays short there.
        aspectRatio: { xs: `${A4_WIDTH_MM} / ${A4_HEIGHT_MM}`, sm: "3 / 2" },
        overflow: "hidden",
        pointerEvents: "none",
        userSelect: "none",
      }}
    >
      {/* Absolute so the sheet cannot hold the frame open as the window shrinks. */}
      <Box
        data-testid="invoice-preview-sheet"
        sx={{
          position: "absolute",
          inset: 0,
          margin: "auto",
          width: sheetWidth,
          height: sheetWidth * A4_RATIO,
          overflow: "hidden",
        }}
      >
        <Box
          ref={pageRef}
          sx={{
            width: "max-content",
            transformOrigin: "top left",
            transform: fit ? `scale(${fit.scale})` : undefined,
            // Unmeasured, the page would flash at full size inside the card.
            visibility: fit ? "visible" : "hidden",
          }}
        >
          <InvoicePreview invoice={invoice} showShadow={false} />
        </Box>
      </Box>
    </Box>
  );
};

export default InvoicePreviewThumbnail;
